import produce from 'immer';
import reactUpdate from 'immutability-helper';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert} from 'Asserts';
import {TagUpdateOptions} from 'file-browser/FinderActions';
import {Tag} from 'folder-items/FileConstants';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import * as BioentityActions from 'inventory/BioentityActions';
import AnalyzeProteinsIcon from 'molbio/components/AnalyzeProteinsIcon';
import notifications from 'notifications';
import isSchemaFieldArchived from 'schemas/fields/isSchemaFieldArchived';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';
import * as TagUtil from 'schemas/tags/TagUtil';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import Spinner from 'ui-library/Spinner';
import {isEnterKey, isEscapeKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import HoistedTagRows from './hoisted-tags/HoistedTagRows';
import TagTable, {BaseTagTableProps} from './TagTable';

interface BaseMetadataTagTableProps extends BaseTagTableProps {
  initialTags: Array<Tag | null>;
  areTagsEditable: boolean;
  updateTags?: (
    inputData: TagUpdateOptions,
    param?: {onFailure: () => void}
  ) => Promise<Array<{id: string; tags: Array<Tag>}> | null>;
  confirmBeforeStartEditing?: () => Promise<boolean>;
  disableEditWithTooltip?: string | null;
  onAnalyzeProteins?: ((proteinIds: Array<string>) => void) | null;
}

/**
 * `showRequiredFieldErrors` controls whether or not we (1) show required field validation errors on
 * schema tags and (2) force the user to provide a value for required schema tags. When enabled, the
 * required error warnings are shown during editing. Update completion is disabled when there are
 * required tags that do not have a value.
 *
 * `showRequiredFieldErrors` cannot be enabled with adding or removing tags. This prop is intended to
 * only be used for schemas that have a static set of tags (see ProcessTaskDetails.tsx).
 */
type Props =
  | (BaseMetadataTagTableProps & {showRequiredFieldErrors: true; canAddOrRemoveTags: false})
  | (BaseMetadataTagTableProps & {showRequiredFieldErrors?: false});

interface State {
  isEditing: boolean;
  tags: Array<Tag | null>;
}

interface SaveUpdate {
  currentTag: Pick<Tag, 'name'> | null;
  updatedTag: (Tag & {tagUpdateParams: Partial<Tag> | null}) | null;
}

/*
  TODO(BNCH-14503): Refactor TagTable to manage edits with useDraftState
    TagTable currently manages state in a way that makes it difficult to change behavior (see #12931).
    This file should be ported to TypeScript and refactored to rely on useDraftState (similar to IngredientTable).
 */
class MetadataTagTable extends React.Component<Props, State> {
  static defaultProps = {
    canAddOrRemoveTags: false,
    canRefreshTags: false,
    errorsByFieldId: {},
    itemLinkAllowed: true,
    fieldsForCustomTagNameValidation: [],
    hoistedTags: [],
    hoistedTagSchemaGraphs: [],
    areTagsEditable: false,
    isLoadingTags: false,
    isNameEditable: false,
    sequenceParts: [],
  };

  override state: State = {
    isEditing: false,
    tags: this.props.initialTags,
  };

  override UNSAFE_componentWillReceiveProps(nextProps: Props): void {
    if (!isEqual(this.props.initialTags, nextProps.initialTags)) {
      this.setState({
        isEditing: false,
        tags: nextProps.initialTags,
      });
    }
  }

  /**
   * Return all writable tags that are not staged for deletion.
   */
  private getEditableTags = (): Array<Tag> =>
    this.state.tags.filter((tag: Tag | null): tag is Tag => tag != null);

  private handleEdit = (): void => {
    this.setState({isEditing: true});
  };

  private handleEditWithConfirmation = async (): Promise<void> => {
    assert(this.props.confirmBeforeStartEditing != null, 'Expected confirmBeforeStartEditing to be provided');
    const shouldProceed = await this.props.confirmBeforeStartEditing();
    if (shouldProceed) {
      this.handleEdit();
    }
  };

  private handleCreateTag = (): void => {
    this.setState({
      isEditing: true,
      tags: reactUpdate(this.state.tags, {
        $push: [
          {
            name: '',
            value: '',
            url: '',
            schemaFieldSelectorOptionIds: null,
            targetBatches: null,
            targetFiles: null,
          },
        ],
      }),
    });
  };

  private getRequiredFieldValidationErrors(): {[fieldId: number]: Array<SchemaValidationErrorRecord>} {
    const requiredSchemaTags = TagUtil.filterToSchemaTags(this.getEditableTags()).filter(
      (schemaTag) => schemaTag.tagSchemaField.isRequired
    );
    return produce(this.props.errorsByFieldId, (draftErrorsByFieldId) => {
      for (const {tagSchemaField} of requiredSchemaTags) {
        const schemaFieldId = tagSchemaField.id;

        const validationError = {
          errorLabel: 'Required value',
          message: `"${tagSchemaField.name}" is a required field.`,
          field: tagSchemaField,
        };
        if (draftErrorsByFieldId.hasOwnProperty(schemaFieldId)) {
          draftErrorsByFieldId[schemaFieldId].push(validationError);
        } else {
          draftErrorsByFieldId[schemaFieldId] = [validationError];
        }
      }
    });
  }

  private handleUpdateTag = (tagIndex: number, newValue: Tag): void => {
    const newTag = reactUpdate(this.state.tags[tagIndex], {$merge: newValue});
    this.setState({tags: reactUpdate(this.state.tags, {$merge: {[tagIndex]: newTag}})});
  };

  private handleRemoveTag = (tagIndex: number): void => {
    if (this.state.isEditing) {
      if (tagIndex >= this.props.initialTags.length) {
        // Discard the tag if it was just created.
        this.setState({tags: reactUpdate(this.state.tags, {$splice: [[tagIndex, 1]]})});
      } else {
        // Otherwise mark it for deletion by setting it to null.
        this.setState({tags: reactUpdate(this.state.tags, {[tagIndex]: {$set: null}})});
      }
    } else {
      this.handleSaveUpdates([{currentTag: this.state.tags[tagIndex], updatedTag: null}]);
    }
  };

  private hasValueForAllRequiredFields(tags: Array<Tag>): boolean {
    return TagUtil.filterToSchemaTags(tags)
      .filter((schemaTag) => schemaTag.tagSchemaField.isRequired)
      .every((requiredSchemaTag) => TagUtil.tagHasValue(requiredSchemaTag));
  }

  private handleCancelUpdate = (): void => {
    this.setState({
      isEditing: false,
      tags: this.props.initialTags,
    });
  };

  private validateTags = (): string | null => {
    const editableTags = this.getEditableTags();
    const tagNames = editableTags.map((tag) => tag.name);
    const existingField = this.props.fieldsForCustomTagNameValidation.find((field) =>
      tagNames.includes(field.name)
    );
    if (existingField != null) {
      if (isSchemaFieldArchived(existingField)) {
        return 'Field name conflicts with a name of an archived field.';
      } else {
        return 'Two fields cannot have the same name.';
      }
    }
    if (tagNames.some((tagName) => !tagName)) {
      return 'Field names cannot be empty.';
    }
    if (tagNames.length !== new Set(tagNames).size) {
      return 'Two fields cannot have the same name.';
    }

    if (this.props.showRequiredFieldErrors && !this.hasValueForAllRequiredFields(editableTags)) {
      return 'Missing values for required fields';
    }

    return null;
  };

  private getUpdatesArray = (): Array<SaveUpdate> => {
    // Construct an updatesArray, [{currentTag, updatedTag}], from the initial tag state and the
    // pending tags. Tags that remain unchanged should not be included. Tags that are created should
    // have {currentTag: null} and tags that are being removed should have {updatedTag: null}.
    const updatesArray = _.zip(this.props.initialTags, this.state.tags).map(([currentTag, updatedTag]) => {
      if (isEqual(currentTag, updatedTag)) {
        return null;
      }
      const currentTagParams = currentTag != null ? TagUtil.getTagUpdateParams(currentTag) : null;
      const updatedTagParams = updatedTag != null ? TagUtil.getTagUpdateParams(updatedTag) : null;
      return {
        currentTag: currentTag
          ? {
              name: currentTag.name,
            }
          : null,
        updatedTag: updatedTag
          ? {
              name: updatedTag.name,
              // Include tagUpdateParams if tag value is has changed.
              tagUpdateParams: !isEqual(currentTagParams, updatedTagParams) ? updatedTagParams : null,
            }
          : null,
      };
    });
    return _.compact(updatesArray);
  };

  private handleCompleteUpdate = (): void => {
    // If updates are invalid, show error and stay in edit mode.
    const error = this.validateTags();
    if (error) {
      notifications.error(error);
      return;
    }
    if (!TagUtil.areTagsValidAndNotify(this.getEditableTags())) {
      return;
    }

    // If updates are valid, save updates.
    this.setState({isEditing: false});
    const updatesArray = this.getUpdatesArray();
    this.handleSaveUpdates(updatesArray);
  };

  private handleSaveUpdates = async (updatesArray: Array<SaveUpdate>): Promise<void> => {
    const toSet: {[key: string]: Partial<Tag>} = {};
    const toRemove: Array<string> = [];
    const toRename: {[key: string]: string} = {};
    const tagNames = updatesArray
      .filter((update: SaveUpdate) => Boolean(update.currentTag))
      .map((update: SaveUpdate) => update.currentTag?.name);
    assert(tagNames.length === new Set(tagNames).size, 'Cannot update the same tag twice');
    updatesArray.forEach(({currentTag, updatedTag}) => {
      if (currentTag == null) {
        // Handle creating custom tag
        if (updatedTag != null && updatedTag.name != null) {
          if (
            _.findWhere(this.props.initialTags, {name: updatedTag.name}) != null ||
            this.props.fieldsForCustomTagNameValidation.some((field) => field.name === updatedTag?.name)
          ) {
            notifications.error('Cannot add two fields with the same name.');
          } else {
            assert(updatedTag.tagUpdateParams != null, 'TagUpdateParams only null on rename action');
            toSet[updatedTag.name] = updatedTag.tagUpdateParams;
          }
        }
      } else if (updatedTag == null) {
        // Delete existing tag if tag no longer exists
        toRemove.push(currentTag.name);
      } else {
        // Update existing tag
        // Update must happen in two parts (set for value, and rename for name)
        if (updatedTag.tagUpdateParams) {
          toSet[currentTag.name] = updatedTag.tagUpdateParams;
        }
        if (updatedTag.name !== currentTag.name) {
          toRename[currentTag.name] = updatedTag.name;
        }
      }
    });
    if (Object.keys(toSet).length > 0 || toRemove.length > 0 || Object.keys(toRename).length > 0) {
      const updatedTags = await this.props.updateTags!(
        {toSet, toRemove, toRename},
        {onFailure: this.handleEdit}
      );
      if (!updatedTags) {
        this.handleEdit();
      }
    }
  };

  private handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (isEnterKey(e)) {
      e.preventDefault();
      this.handleCompleteUpdate();
    } else if (isEscapeKey(e)) {
      e.preventDefault();
      this.handleCancelUpdate();
    }
  };

  private getAdditionalHeaderIcons = (): React.ReactNode => (
    <AnalyzeProteinsIcon
      onAnalyzeProteins={this.props.onAnalyzeProteins}
      tags={this.state.tags}
      hoistedTags={this.props.hoistedTags}
      fileId={this.props.fileId}
    />
  );

  override render(): JSX.Element {
    return (
      <TagTable
        canAddOrRemoveTags={this.props.canAddOrRemoveTags}
        canRefreshTags={this.props.canRefreshTags}
        emptyMessage={this.props.emptyMessage}
        errorsByFieldId={
          this.state.isEditing && this.props.showRequiredFieldErrors
            ? this.getRequiredFieldValidationErrors()
            : this.props.errorsByFieldId
        }
        fileId={this.props.fileId}
        itemLinkAllowed={this.props.itemLinkAllowed}
        hoistedTags={this.props.hoistedTags}
        hoistedTagSchemaGraphs={this.props.hoistedTagSchemaGraphs}
        tags={this.state.tags}
        areTagsEditable={this.props.areTagsEditable}
        isLoadingTags={this.props.isLoadingTags}
        isNameEditable={this.props.isNameEditable}
        additionalHeaderIcons={this.getAdditionalHeaderIcons()}
        sequenceParts={this.props.sequenceParts}
        subheaderNode={this.props.subheaderNode}
        title={this.props.title}
        isEditing={this.state.isEditing}
        onUpdateTag={this.handleUpdateTag}
        onCreateTag={this.handleCreateTag}
        onRemoveTag={this.handleRemoveTag}
        onEdit={
          this.props.confirmBeforeStartEditing != null ? this.handleEditWithConfirmation : this.handleEdit
        }
        onCompleteUpdate={this.handleCompleteUpdate}
        onCancelUpdate={this.handleCancelUpdate}
        onKeyDown={this.handleKeyDown}
        renderAutofillButton={this.props.renderAutofillButton}
        disableEditWithTooltip={this.props.disableEditWithTooltip}
      />
    );
  }
}

export default MetadataTagTable;
